/**
 * Nation Wars Share Engine — turns every share into a national challenge.
 * - Personalized URL: /nation-wars?nation=XX
 * - Personalized message per nation (with rival hook)
 * - Differentiated channels: native Web Share API vs direct WhatsApp deep-link
 * - Tracks: share_source, share_target, nation_code, share_platform
 *
 * Pure helper. No new engines, no polling, no realtime.
 */
import { trackEvent } from "@/lib/audit";
import { getStoredLang, type Lang } from "@/lib/i18n";
import { PREVIEW_MODE } from "@/lib/previewMode";

export type NationSharePlatform = "native" | "whatsapp" | "copy";

const BASE_URL = "https://versesport.com";

/** UTM tagging for every outbound share. */
export type NationShareUtm = {
  source: string; // where the share button lives
  medium?: string; // default: "share"
  campaign?: string; // default: "nation_wars"
  platform?: NationSharePlatform; // utm_content fallback
  nation?: string | null;
};

function appendUtm(url: string, utm: NationShareUtm): string {
  try {
    const u = new URL(url);
    u.searchParams.set("utm_source", utm.source);
    u.searchParams.set("utm_medium", utm.medium ?? "share");
    u.searchParams.set("utm_campaign", utm.campaign ?? "nation_wars");
    if (utm.platform) u.searchParams.set("utm_content", utm.platform);
    if (utm.nation) u.searchParams.set("utm_term", utm.nation);
    return u.toString();
  } catch {
    return url;
  }
}

/** Public helper so any share UI can decorate its links with consistent UTMs. */
export function withShareUtm(url: string, utm: NationShareUtm): string {
  return appendUtm(url, utm);
}

/**
 * Nation-specific challenge messages, localized per Lang.
 * Falls back to a generic global hook in the requested language.
 * ISO-3 codes are aliased to ISO-2 entries so `profiles.selection_team`
 * (MEX/ARG/BRA/USA…) is supported without lossy conversions.
 */
type NationMsgMap = Record<string, string>;

const NATION_MESSAGES_ES: NationMsgMap = {
  BR: "🇧🇷 Brasil está peleando por el #1 mundial. Argentina viene acercándose. ¿Vas a dejar que te superen?",
  AR: "🇦🇷 Argentina lidera la batalla mundial. Brasil está atacando fuerte. Defiende tu bandera.",
  MX: "🇲🇽 México está subiendo en el ranking. Miles de aficionados ya están sumando puntos. Únete ahora.",
  CO: "🇨🇴 Colombia escala posiciones en Nation Wars. Cada predicción cuenta. Suma puntos por tu bandera.",
  EC: "🇪🇨 Ecuador defiende el orgullo nacional. Cada acierto suma. Ayuda a tu nación a subir.",
  US: "🇺🇸 USA está peleando por la cima mundial. México viene fuerte. Defiende tus colores.",
  UY: "🇺🇾 Uruguay no se rinde. Suma puntos por la celeste en la batalla mundial.",
  CL: "🇨🇱 Chile escala en Nation Wars. Únete y representa la roja.",
  PE: "🇵🇪 Perú está empujando en el ranking mundial. Cada predicción cuenta.",
  ES: "🇪🇸 España pelea por liderar la batalla mundial. Defiende la roja.",
  PT: "🇵🇹 Portugal está atacando en el ranking. Cada predicción suma. ¡Vamos!",
  FR: "🇫🇷 Francia lucha por la cima mundial. Defiende los bleus.",
  DE: "🇩🇪 Alemania escala en Nation Wars. Suma puntos por la Mannschaft.",
  IT: "🇮🇹 Italia regresa a la batalla mundial. Defiende los azzurri.",
  EN: "🏴 Inglaterra escala el ranking mundial. Predice y defiende a los Three Lions.",
};

const NATION_MESSAGES_EN: NationMsgMap = {
  BR: "🇧🇷 Brazil is fighting for the world #1. Argentina is closing in. Will you let them take over?",
  AR: "🇦🇷 Argentina leads the global battle. Brazil is attacking hard. Defend your flag.",
  MX: "🇲🇽 Mexico is climbing the ranking. Thousands of fans are already scoring points. Join now.",
  CO: "🇨🇴 Colombia is climbing in Nation Wars. Every prediction counts. Score points for your flag.",
  EC: "🇪🇨 Ecuador defends national pride. Every correct pick adds up. Help your nation rise.",
  US: "🇺🇸 USA is fighting for the world top. Mexico is coming strong. Defend your colors.",
  UY: "🇺🇾 Uruguay never quits. Score points for La Celeste in the global battle.",
  CL: "🇨🇱 Chile is climbing in Nation Wars. Join in and represent La Roja.",
  PE: "🇵🇪 Peru is pushing up the world ranking. Every prediction counts.",
  ES: "🇪🇸 Spain fights to lead the global battle. Defend La Roja.",
  PT: "🇵🇹 Portugal is attacking the ranking. Every prediction counts. Let's go!",
  FR: "🇫🇷 France battles for the world top. Defend Les Bleus.",
  DE: "🇩🇪 Germany is climbing in Nation Wars. Score points for Die Mannschaft.",
  IT: "🇮🇹 Italy returns to the global battle. Defend the Azzurri.",
  EN: "🏴 England climbs the global ranking. Predict and defend the Three Lions.",
};

const NATION_MESSAGES_PT: NationMsgMap = {
  BR: "🇧🇷 O Brasil luta pelo #1 do mundo. A Argentina vem chegando. Vai deixar passar?",
  AR: "🇦🇷 A Argentina lidera a batalha mundial. O Brasil ataca forte. Defenda sua bandeira.",
  MX: "🇲🇽 O México sobe no ranking. Milhares de torcedores já estão somando pontos. Entre agora.",
  CO: "🇨🇴 A Colômbia sobe posições em Nation Wars. Cada palpite conta. Some pontos pela sua bandeira.",
  EC: "🇪🇨 O Equador defende o orgulho nacional. Cada acerto soma. Ajude sua nação a subir.",
  US: "🇺🇸 Os EUA brigam pelo topo mundial. O México vem forte. Defenda suas cores.",
  UY: "🇺🇾 O Uruguai não se rende. Some pontos pela Celeste na batalha mundial.",
  CL: "🇨🇱 O Chile sobe em Nation Wars. Entre e represente La Roja.",
  PE: "🇵🇪 O Peru avança no ranking mundial. Cada palpite conta.",
  ES: "🇪🇸 A Espanha luta para liderar a batalha mundial. Defenda La Roja.",
  PT: "🇵🇹 Portugal está atacando no ranking. Cada palpite soma. Vamos!",
  FR: "🇫🇷 A França luta pelo topo mundial. Defenda Les Bleus.",
  DE: "🇩🇪 A Alemanha sobe em Nation Wars. Some pontos pela Mannschaft.",
  IT: "🇮🇹 A Itália volta à batalha mundial. Defenda os Azzurri.",
  EN: "🏴 A Inglaterra sobe no ranking mundial. Preveja e defenda os Three Lions.",
};

const ISO3_TO_ISO2: Record<string, string> = {
  MEX: "MX", ARG: "AR", BRA: "BR", COL: "CO", ECU: "EC", USA: "US",
  URY: "UY", CHL: "CL", PER: "PE", ESP: "ES", POR: "PT", FRA: "FR",
  GER: "DE", ITA: "IT", ENG: "EN",
};
for (const [iso3, iso2] of Object.entries(ISO3_TO_ISO2)) {
  NATION_MESSAGES_ES[iso3] = NATION_MESSAGES_ES[iso2];
  NATION_MESSAGES_EN[iso3] = NATION_MESSAGES_EN[iso2];
  NATION_MESSAGES_PT[iso3] = NATION_MESSAGES_PT[iso2];
}

const NATION_MESSAGES_BY_LANG: Record<Lang, NationMsgMap> = {
  ES: NATION_MESSAGES_ES,
  EN: NATION_MESSAGES_EN,
  PT: NATION_MESSAGES_PT,
};

const GENERIC_FALLBACK: Record<Lang, string> = {
  ES: "🌎 La batalla mundial de naciones está en marcha. Elige tu bandera, suma puntos y escala el ranking.",
  EN: "🌎 The global battle of nations is on. Pick your flag, score points and climb the ranking.",
  PT: "🌎 A batalha mundial de nações está rolando. Escolha sua bandeira, some pontos e suba no ranking.",
};

function resolveLang(lang?: Lang): Lang {
  return lang ?? getStoredLang();
}

const FLAGS: Record<string, string> = {
  BR: "🇧🇷", AR: "🇦🇷", MX: "🇲🇽", CO: "🇨🇴", EC: "🇪🇨", US: "🇺🇸",
  UY: "🇺🇾", CL: "🇨🇱", PE: "🇵🇪", ES: "🇪🇸", PT: "🇵🇹", FR: "🇫🇷",
  DE: "🇩🇪", IT: "🇮🇹", EN: "🏴",
  MEX: "🇲🇽", ARG: "🇦🇷", BRA: "🇧🇷", COL: "🇨🇴", ECU: "🇪🇨", USA: "🇺🇸",
  URY: "🇺🇾", CHL: "🇨🇱", PER: "🇵🇪", ESP: "🇪🇸", POR: "🇵🇹", FRA: "🇫🇷",
  GER: "🇩🇪", ITA: "🇮🇹", ENG: "🏴",
};

const NAMES_ES: Record<string, string> = {
  BR: "Brasil", AR: "Argentina", MX: "México", CO: "Colombia", EC: "Ecuador",
  US: "USA", UY: "Uruguay", CL: "Chile", PE: "Perú", ES: "España", PT: "Portugal",
  FR: "Francia", DE: "Alemania", IT: "Italia", EN: "Inglaterra",
};
const NAMES_EN: Record<string, string> = {
  BR: "Brazil", AR: "Argentina", MX: "Mexico", CO: "Colombia", EC: "Ecuador",
  US: "USA", UY: "Uruguay", CL: "Chile", PE: "Peru", ES: "Spain", PT: "Portugal",
  FR: "France", DE: "Germany", IT: "Italy", EN: "England",
};
const NAMES_PT: Record<string, string> = {
  BR: "Brasil", AR: "Argentina", MX: "México", CO: "Colômbia", EC: "Equador",
  US: "EUA", UY: "Uruguai", CL: "Chile", PE: "Peru", ES: "Espanha", PT: "Portugal",
  FR: "França", DE: "Alemanha", IT: "Itália", EN: "Inglaterra",
};
for (const [iso3, iso2] of Object.entries(ISO3_TO_ISO2)) {
  NAMES_ES[iso3] = NAMES_ES[iso2];
  NAMES_EN[iso3] = NAMES_EN[iso2];
  NAMES_PT[iso3] = NAMES_PT[iso2];
}
const NAMES_BY_LANG: Record<Lang, Record<string, string>> = {
  ES: NAMES_ES, EN: NAMES_EN, PT: NAMES_PT,
};
const FALLBACK_NATION_LABEL: Record<Lang, string> = {
  ES: "Tu nación", EN: "Your nation", PT: "Sua nação",
};

function normalize(code?: string | null): string | null {
  if (!code) return null;
  const c = code.trim().toUpperCase();
  return c.length >= 2 ? c : null;
}

export function buildNationWarsUrl(nationCode?: string | null): string {
  const code = normalize(nationCode);
  return code ? `${BASE_URL}/nation-wars?nation=${code}` : `${BASE_URL}/nation-wars`;
}

/** Short, share-friendly landing URL with dynamic OG: /s/:nation */
export function buildNationShareLandingUrl(nationCode?: string | null): string {
  const code = normalize(nationCode);
  return code ? `${BASE_URL}/s/${code}` : `${BASE_URL}/nation-wars`;
}

export function buildNationWarsMessage(nationCode?: string | null, lang?: Lang): string {
  const code = normalize(nationCode);
  const L = resolveLang(lang);
  // PREVIEW MODE — Final del Mundial ESP vs ARG. Sobrescribimos los mensajes
  // por nación para eliminar cualquier referencia residual (Brasil, México)
  // y garantizar la misma calidad de copy para ambas aficiones.
  if (PREVIEW_MODE && code) {
      const c = code.toUpperCase();
      const isESP = c === "ES" || c === "ESP";
      const isARG = c === "AR" || c === "ARG";
      if (isESP) {
        return L === "EN"
          ? "🇪🇸 Spain fights for the World Cup Final. Defend La Roja against Argentina."
          : L === "PT"
          ? "🇪🇸 Espanha luta pela Final da Copa. Defenda La Roja contra a Argentina."
          : "🇪🇸 España pelea por la Final del Mundial. Defiende La Roja frente a Argentina.";
      }
      if (isARG) {
        return L === "EN"
          ? "🇦🇷 Argentina goes for the World Cup Final. Defend La Albiceleste against Spain."
          : L === "PT"
          ? "🇦🇷 Argentina vai pela Final da Copa. Defenda La Albiceleste contra a Espanha."
          : "🇦🇷 Argentina va por la Final del Mundial. Defiende La Albiceleste frente a España.";
      }
  }
  const map = NATION_MESSAGES_BY_LANG[L];
  if (code && map[code]) return map[code];
  return GENERIC_FALLBACK[L];
}

export function getNationFlag(nationCode?: string | null): string {
  const code = normalize(nationCode);
  return (code && FLAGS[code]) || "🌎";
}

export function getNationName(nationCode?: string | null, lang?: Lang): string {
  const code = normalize(nationCode);
  const L = resolveLang(lang);
  return (code && NAMES_BY_LANG[L][code]) || FALLBACK_NATION_LABEL[L];
}

const OG_TITLE_GENERIC: Record<Lang, string> = {
  ES: "Nation Wars — La batalla mundial por el #1",
  EN: "Nation Wars — The global battle for #1",
  PT: "Nation Wars — A batalha mundial pelo #1",
};
const OG_TITLE_NATION: Record<Lang, (flag: string, name: string) => string> = {
  ES: (f, n) => `${f} ${n} pelea por el #1 Mundial`,
  EN: (f, n) => `${f} ${n} fights for the World #1`,
  PT: (f, n) => `${f} ${n} luta pelo #1 Mundial`,
};
const OG_DESCRIPTION_BY_LANG: Record<Lang, string> = {
  ES: "Únete a tu nación y gana la batalla mundial. Cada predicción suma puntos por tu bandera.",
  EN: "Join your nation and win the global battle. Every prediction scores points for your flag.",
  PT: "Junte-se à sua nação e vença a batalha mundial. Cada palpite soma pontos pela sua bandeira.",
};

export function buildOgTitle(nationCode?: string | null, lang?: Lang): string {
  const code = normalize(nationCode);
  const L = resolveLang(lang);
  if (!code) return OG_TITLE_GENERIC[L];
  return OG_TITLE_NATION[L](getNationFlag(code), getNationName(code, L));
}

export function getNationWarsOgDescription(lang?: Lang): string {
  return OG_DESCRIPTION_BY_LANG[resolveLang(lang)];
}

/** @deprecated Use getNationWarsOgDescription(lang) — kept for SSR head metadata. */
export const NATION_WARS_OG_DESCRIPTION = OG_DESCRIPTION_BY_LANG.ES;

/**
 * Static OG/Twitter share images per nation. Falls back to the global Mundial
 * card for nations without a dedicated asset. Files live under /public/og/.
 */
const NATION_OG_IMAGES: Record<string, string> = {
  BR: "/og/nation-br.jpg",
  AR: "/og/nation-ar.jpg",
  MX: "/og/nation-mx.jpg",
  CO: "/og/nation-co.jpg",
  US: "/og/nation-us.jpg",
  // ISO-3 aliases — preferred by share consumers reading selection_team.
  BRA: "/og/nation-bra.jpg",
  ARG: "/og/nation-arg.jpg",
  MEX: "/og/nation-mex.jpg",
  COL: "/og/nation-col.jpg",
  USA: "/og/nation-usa.jpg",
};

export const MUNDIAL_OG_IMAGE_PATH = "/og/mundial-2026.jpg";

/** Absolute URL for the OG image associated with a nation (or generic Mundial). */
export function getNationOgImageUrl(nationCode?: string | null): string {
  const code = normalize(nationCode);
  const path = (code && NATION_OG_IMAGES[code]) || MUNDIAL_OG_IMAGE_PATH;
  return `${BASE_URL}${path}`;
}

async function track(
  platform: NationSharePlatform,
  opts: { source: string; nation: string | null },
) {
  const eventType =
    platform === "native"
      ? "nation_wars_share_native"
      : platform === "whatsapp"
      ? "nation_wars_share_whatsapp"
      : "nation_wars_share_copy";
  try {
    await trackEvent(eventType, {
      share_source: opts.source,
      share_target: "nation-wars",
      share_platform: platform,
      nation_code: opts.nation,
    });
  } catch {
    /* non-fatal */
  }
}

/** Web Share API; falls back to clipboard copy. */
export async function shareNationWarsNative(opts: {
  source: string;
  nation?: string | null;
  lang?: Lang;
}): Promise<{ ok: boolean; fallback?: "copy" | "none" }> {
  const nation = normalize(opts.nation);
  const lang = resolveLang(opts.lang);
  const url = buildNationWarsUrl(nation);
  const text = buildNationWarsMessage(nation, lang);
  const title = buildOgTitle(nation, lang);

  const nav =
    typeof navigator !== "undefined"
      ? (navigator as Navigator & { share?: (d: ShareData) => Promise<void> })
      : undefined;

  if (nav?.share) {
    try {
      const taggedUrl = appendUtm(url, { source: opts.source, platform: "native", nation });
      await nav.share({ title, text, url: taggedUrl });
      await track("native", { source: opts.source, nation });
      return { ok: true };
    } catch {
      /* user cancelled — fall through to copy */
    }
  }
  try {
    const taggedUrl = appendUtm(url, { source: opts.source, platform: "copy", nation });
    await navigator.clipboard.writeText(`${text} ${taggedUrl}`);
    await track("copy", { source: opts.source, nation });
    return { ok: true, fallback: "copy" };
  } catch {
    return { ok: false, fallback: "none" };
  }
}

/** Direct WhatsApp deep-link in a new tab. */
export function shareNationWarsWhatsApp(opts: {
  source: string;
  nation?: string | null;
  lang?: Lang;
}): void {
  const nation = normalize(opts.nation);
  const lang = resolveLang(opts.lang);
  const url = appendUtm(buildNationWarsUrl(nation), {
    source: opts.source,
    platform: "whatsapp",
    nation,
  });
  const text = buildNationWarsMessage(nation, lang);
  const href = `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`;
  if (typeof window !== "undefined") {
    window.open(href, "_blank", "noopener,noreferrer");
  }
  void track("whatsapp", { source: opts.source, nation });
}
